import { useParams } from "react-router-dom";
import { useGetFinalizadoQuery } from "../../../services/docs";
import { Spinner } from "../../../components/Spinner";

export const FinalizadoScreen = () => {
  const { siniestro, tramite_id = "0" } = useParams();
  const { data, isLoading } = useGetFinalizadoQuery({
    siniestro,
    tramite: tramite_id,
  });

  if (isLoading) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // Prevenimos errores si no hay datos
  const finalData = data?.finalizado?.[0];

  if (!finalData) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50/50 py-12 text-center">
        <svg className="mb-3 h-8 w-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
        <p className="text-sm font-bold text-slate-500">Información no disponible</p>
        <p className="mt-1 text-xs text-slate-400">No se encontraron detalles de la finalización de este trámite.</p>
      </div>
    );
  }

  const isEliminado = finalData.fi_rechazo === 1;

  return (
    <div className="space-y-6 pt-4">
      <div className="mx-auto max-w-4xl">

        {/* =========================================
            VISTA: TRÁMITE ELIMINADO / RECHAZADO
            ========================================= */}
        {isEliminado && (
          <section className="overflow-hidden rounded-xl border border-rose-200 bg-white shadow-sm">
            <div className="border-b border-rose-100 bg-rose-50/80 px-6 py-4">
              <h2 className="flex items-center gap-2 text-sm font-bold text-rose-800">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-white text-rose-600 shadow-sm">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                </div>
                Trámite Cancelado / Eliminado
              </h2>
            </div>

            <div className="p-6">
              <div className="rounded-xl border border-rose-100 bg-rose-50/50 p-5">
                <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-rose-500">
                  Fase de Cancelación
                </span>
                <span className="inline-block rounded-md bg-white border border-rose-200 px-2.5 py-1 text-xs font-semibold text-rose-700 shadow-sm">
                  {finalData.fc_sub_etapa}
                </span>

                <div className="mt-5 border-t border-rose-100 pt-4">
                  <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-rose-500">
                    Motivo de Eliminación Registrado
                  </span>
                  <p className="text-sm font-medium text-slate-800">
                    {finalData.t_fc_motivo_eliminacion}
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* =========================================
            VISTA: TRÁMITE FINALIZADO (RESOLUCIÓN)
            ========================================= */}
        {!isEliminado && (
          <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">

            {/* Cabecera */}
            <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-sm font-bold text-slate-800">
                  <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                  </div>
                  Resolución Final del Trámite
                </h2>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                  Trámite Cerrado
                </span>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 gap-8 md:grid-cols-12">

                {/* Columna Izquierda: Datos del Dictamen */}
                <div className="md:col-span-8 flex flex-col gap-6">
                  <div>
                    <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500">
                      Dictamen de la Aseguradora
                    </span>
                    <h3 className="text-2xl font-black text-slate-900 md:text-3xl">
                      {finalData.d_fc_respuesta_aseguradora}
                    </h3>
                  </div>

                  {/* Tarjeta de Pago (Aparece solo si fue pago) */}
                  {finalData.d_fi_respuesta_aseguradora === 1 && (
                    <div className="flex items-center gap-4 rounded-xl border border-emerald-200 bg-emerald-50/80 p-4">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-200/50 text-emerald-600">
                        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                      </div>
                      <div>
                        <span className="block text-[11px] font-bold uppercase tracking-wider text-emerald-800">
                          Monto Pagado / Indemnización
                        </span>
                        <span className="text-xl font-bold text-emerald-900">
                          {finalData.d_fn_monto_pagado ? `$ ${finalData.d_fn_monto_pagado}` : "N/A"}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Comentarios */}
                  <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                    <span className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-500">
                      Comentarios y Observaciones del Dictamen
                    </span>
                    <p className="text-sm font-medium leading-relaxed text-slate-700">
                      {finalData.d_fc_comentarios_dictamen || "Sin comentarios adicionales."}
                    </p>
                  </div>
                </div>

                {/* Columna Derecha: Carta Dictamen */}
                <div className="md:col-span-4 flex flex-col justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 p-6 text-center">
                  <svg className="mx-auto mb-3 h-10 w-10 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                  <h4 className="text-sm font-bold text-slate-800">Carta Dictamen Oficial</h4>
                  <p className="mt-1 mb-4 text-[11px] text-slate-500">
                    Documento emitido por la aseguradora con la resolución del caso.
                  </p>

                  {finalData.d_fc_url_carta ? (
                    <a
                      href={finalData.d_fc_url_carta}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-brand-700"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
                      Ver PDF Original
                    </a>
                  ) : (
                    <span className="inline-block w-full rounded-xl border border-slate-200 bg-slate-100 px-4 py-2.5 text-xs font-semibold text-slate-400 cursor-not-allowed">
                      Archivo no disponible
                    </span>
                  )}
                </div>

              </div>
            </div>
          </section>
        )}

      </div>
    </div>
  );
};
